import { useQuery } from '@tanstack/react-query'
import { useRetainedRealtimeState } from '../../services/firebase/use-retained-realtime-state'
import { useEffect, useMemo, useRef } from 'react'

import {
  appointmentReadError,
  type AppointmentWindowRepository,
} from './appointments-repository'
import {
  buildAppointmentWindow,
  parseAppointmentWindow,
  type AppointmentWindow,
} from './appointment-window'
import type { AppointmentItem } from './appointments-model'

type State = {
  key: string
  status: 'loading' | 'ready' | 'forbidden' | 'error' | 'invalid'
  appointments: AppointmentItem[]
}

type TimeZoneResult = {
  isError: boolean
  isSuccess: boolean
  error: unknown
  timeZone: string | null
}

type AppointmentWindowResult = State & {
  timeZone: string | null
  window: AppointmentWindow | null
}

export function useAppointmentTimeZone(
  repository: AppointmentWindowRepository,
  tenantId: string
): TimeZoneResult {
  const query = useQuery({
    queryKey: ['appointment-window', tenantId, 'time-zone'],
    queryFn: () => repository.getClinicTimeZone(tenantId),
    staleTime: 30_000,
  })
  const timeZone =
    !query.isError && typeof query.data === 'string' && query.data.trim()
      ? query.data
      : null
  return {
    isError: query.isError,
    isSuccess: query.isSuccess,
    error: query.error,
    timeZone,
  }
}

export function useAppointmentWindow(
  repository: AppointmentWindowRepository,
  tenantId: string,
  dates: string[]
): AppointmentWindowResult {
  const generation = useRef(0)
  const zone = useAppointmentTimeZone(repository, tenantId)
  const timeZone = zone.timeZone
  const datesKey = dates.join(':')
  const stableDates = useMemo(
    () => datesKey.split(':').filter(Boolean),
    [datesKey]
  )
  const window = useMemo(
    () => (timeZone ? buildAppointmentWindow(stableDates, timeZone) : null),
    [stableDates, timeZone]
  )
  const requestKey = `${tenantId}:${datesKey}`
  const key = window
    ? `${tenantId}:${window.startAtMs}:${window.endBeforeMs}:${window.timeZone}`
    : requestKey
  const [state, setState, resume] = useRetainedRealtimeState<State>(
    ['calendar', tenantId, key],
    {
      key,
      status: 'loading',
      appointments: [],
    },
    (value) => value.status === 'ready'
  )

  useEffect(() => {
    const current = ++generation.current
    resume()
    if (!window) return
    const unsubscribe = repository.subscribeAppointmentWindow(
      tenantId,
      window.startAtMs,
      window.endBeforeMs,
      (value) => {
        if (generation.current !== current) return
        const page = parseAppointmentWindow(value, window)
        setState({
          key,
          status: page.valid ? 'ready' : 'invalid',
          appointments: page.valid ? page.items : [],
        })
      },
      (error) => {
        if (generation.current !== current) return
        setState({
          key,
          status:
            appointmentReadError(error) === 'forbidden' ? 'forbidden' : 'error',
          appointments: [],
        })
      }
    )
    return () => {
      generation.current += 1
      unsubscribe()
    }
  }, [key, repository, tenantId, window, resume, setState])

  if (zone.isError || (zone.isSuccess && !timeZone)) {
    return {
      key,
      status: zone.isError
        ? appointmentReadError(zone.error) === 'forbidden'
          ? ('forbidden' as const)
          : ('error' as const)
        : ('invalid' as const),
      appointments: [],
      timeZone: null,
      window: null,
    }
  }
  return state.key === key
    ? { ...state, timeZone, window }
    : {
        key,
        status: 'loading' as const,
        appointments: [] as AppointmentItem[],
        timeZone,
        window,
      }
}
